import { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SunIcon, MoonIcon } from '@heroicons/react/24/outline';
import LanguageSwitcher from './LanguageSwitcher';
import Logo from './Logo';
import { trackNavigation, trackThemeToggle } from '../utils/analytics';

interface HeaderProps {
  darkMode: boolean;
  setDarkMode: (dark: boolean) => void;
}

export default function Header({ darkMode, setDarkMode }: HeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { href: '/about', label: t('nav.about') },
    { href: '/projects', label: t('nav.projects') },
    { href: '/skills', label: t('nav.skills') },
    { href: '/testimonials', label: t('nav.testimonials') },
    { href: '/services/legacy-frontend-rescue', label: t('nav.legacyRescue'), isCta: true },
    { href: '/contact', label: t('nav.contact') },
  ];

  const handleNavigationClick = (linkText: string, linkUrl: string) => {
    trackNavigation(linkText, linkUrl, 'header');
  };

  const handleThemeToggle = () => {
    const newTheme = !darkMode;
    setDarkMode(newTheme);
    trackThemeToggle(newTheme ? 'dark' : 'light');
  };

  const handleMobileMenuToggle = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <header
      className={`sticky top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/85 dark:bg-gray-900/85 backdrop-blur-md border-b border-gray-200/60 dark:border-gray-800/60 shadow-sm shadow-gray-900/5'
          : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto max-w-6xl px-4">
        <div className="flex items-center justify-between h-16 md:h-[4.5rem]">
          {/* Logo */}
          <Link
            to="/"
            className="pointer-events-auto text-xl font-bold text-gray-950 dark:text-white hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
            aria-label="Home"
            onClick={() => handleNavigationClick('Logo', '/')}
          >
            <Logo darkMode={darkMode} />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-2" role="navigation" aria-label="Main navigation">
            {navItems.map((item) => (
              <NavLink
                key={item.href}
                to={item.href}
                end
                className={({ isActive }) => {
                  const standard = isActive ? 'nav-link--active' : 'nav-link';
                  const ctaBase = item.isCta ? 'nav-link__cta' : '';
                  const ctaActive = 'nav-link__cta--active';
                  return `nav-link ${item.isCta ? `${ctaBase} ${isActive ? ctaActive : ''}` : standard}`;
                }}
                onClick={() => handleNavigationClick(item.label, item.href)}
                tabIndex={0}
              >
                {item.label}
              </NavLink>
            ))}

            <div className="flex items-center gap-2 ml-3 pl-3 border-l border-gray-200/60 dark:border-gray-700/60">
              <LanguageSwitcher />
              <button
                type="button"
                onClick={handleThemeToggle}
                className="header-utility-btn"
                aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                aria-pressed={darkMode}
              >
                {darkMode ? (
                  <SunIcon className="w-4 h-4" aria-hidden="true" />
                ) : (
                  <MoonIcon className="w-4 h-4" aria-hidden="true" />
                )}
              </button>
            </div>
          </nav>

          {/* Mobile Menu Button */}
          <div className="flex md:hidden items-center gap-2">
            <LanguageSwitcher />
            <button
              type="button"
              onClick={handleThemeToggle}
              className="header-utility-btn"
              aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              aria-pressed={darkMode}
            >
              {darkMode ? (
                <SunIcon className="w-4 h-4" aria-hidden="true" />
              ) : (
                <MoonIcon className="w-4 h-4" aria-hidden="true" />
              )}
            </button>
            <button
              type="button"
              onClick={handleMobileMenuToggle}
              className="header-utility-btn"
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-menu"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav
            className="md:hidden py-4 border-t border-gray-200/60 dark:border-gray-700/60"
            role="navigation"
            aria-label="Mobile navigation"
            id="mobile-menu"
          >
            <div className="flex flex-col gap-1.5">
              {navItems.map((item) => (
                <NavLink
                  key={item.href}
                  to={item.href}
                  end
                  onClick={() => {
                    setIsMenuOpen(false);
                    handleNavigationClick(item.label, item.href);
                  }}
                  className={({ isActive }) => {
                    const standard = isActive ? 'nav-link--active' : 'nav-link';
                    const ctaBase = item.isCta ? 'nav-link__cta' : '';
                    const ctaActive = 'nav-link__cta--active';
                    return `nav-link ${item.isCta ? `${ctaBase} ${isActive ? ctaActive : ''}` : standard}`;
                  }}
                >
                  {item.label}
                </NavLink>
              ))}
            </div>
          </nav>
        )}
      </div>
    </header>
  );
}
